import { Slot } from 'radix-ui';
import { composeEventHandlers, useComposedRefs } from 'radix-ui/internal';
import type { ComponentProps, PointerEvent as ReactPointerEvent, ReactElement } from 'react';

import { type PlainTooltipEventKind, TooltipEventKind, TooltipMode } from '@/types/tooltip';

import { useTooltipContext } from './tooltipContext';

const claimedMoves = new WeakSet<Event>();

type Props = ComponentProps<'button'> & {
    asChild?: boolean;
};

export const TooltipTrigger = ({
    asChild,
    ref: forwardedRef,
    ...props
}: Props): ReactElement => {
    const { state, mode, contentId, triggerRef, send } = useTooltipContext('TooltipTrigger');
    const ref = useComposedRefs(forwardedRef, triggerRef);
    const Comp = asChild ? Slot.Root : 'button';
    const onPointerMove = (event: ReactPointerEvent): void => {
        if (event.pointerType === 'mouse') {
            if (claimedMoves.has(event.nativeEvent)) {
                return;
            }
            claimedMoves.add(event.nativeEvent);
        }
        send({ kind: TooltipEventKind.PointerMove, isTouch: event.pointerType === 'touch' });
    };
    const sendOn = (kind: PlainTooltipEventKind): (() => void) => (): void => send({ kind });
    return (
        <Comp
            data-slot="tooltip-trigger"
            aria-describedby={state.isOpen ? contentId : undefined}
            {...props}
            {...(mode === TooltipMode.Inert ? {} : {
                onPointerEnter: composeEventHandlers(
                    props.onPointerEnter, sendOn(TooltipEventKind.PointerEnter)),
                onPointerMove: composeEventHandlers(props.onPointerMove, onPointerMove),
                onPointerLeave: composeEventHandlers(
                    props.onPointerLeave, sendOn(TooltipEventKind.PointerLeave)),
                onPointerDown: composeEventHandlers(
                    props.onPointerDown, sendOn(TooltipEventKind.PointerDown)),
                onFocus: composeEventHandlers(props.onFocus, sendOn(TooltipEventKind.Focus)),
                onBlur: composeEventHandlers(props.onBlur, sendOn(TooltipEventKind.Blur)),
                onClick: composeEventHandlers(props.onClick, sendOn(TooltipEventKind.Click)),
            })}
            ref={ref}
        />
    );
};
